/**
 * File items-filter.js.
 *
 * Items List filtering.
 *
 * @package wildrivers
 */
import { WPAjax } from './wpajax';

/**
 * Escape a string for use in a CSS attribute selector (data-term value).
 *
 * @param {string} value Raw term slug from URL.
 * @return {string}
 */
function escapeAttrSelector( value ) {
	if ( typeof CSS !== 'undefined' && typeof CSS.escape === 'function' ) {
		return CSS.escape( value );
	}
	return String( value ).replace( /\\/g, '\\\\' ).replace( /"/g, '\\"' );
}

/**
 * Update ?filter= to match the active term without reloading.
 *
 * @param {string} term Same value as link.dataset.term (e.g. slug or "all").
 * @return {void}
 */
function syncFilterQueryParam( term ) {
	const params = new URLSearchParams( window.location.search );
	params.set( 'filter', term );
	const query = params.toString();
	const newUrl = window.location.pathname + ( query ? `?${ query }` : '' ) + window.location.hash;
	window.history.replaceState( null, '', newUrl );
}

/**
 * Remember the selected term for this taxonomy (key: {taxonomy}_filter).
 *
 * @param {string} taxonomyName Value from filters.dataset.taxonomy.
 * @param {string} term         Same value as link.dataset.term.
 * @return {void}
 */
function persistFilterForTaxonomy( taxonomyName, term ) {
	if ( ! taxonomyName || typeof localStorage === 'undefined' ) {
		return;
	}
	try {
		localStorage.setItem( `${ taxonomyName }_filter`, term );
	} catch {
		// Private mode, quota, or disabled storage — ignore.
	}
}

/**
 * Read a previously saved term for this taxonomy.
 *
 * @param {string} taxonomyName Value from filters.dataset.taxonomy.
 * @return {string|null} Stored term, or null if missing / unreadable.
 */
function getStoredFilterForTaxonomy( taxonomyName ) {
	if ( ! taxonomyName || typeof localStorage === 'undefined' ) {
		return null;
	}
	try {
		const value = localStorage.getItem( `${ taxonomyName }_filter` );
		if ( value === null || value === '' ) {
			return null;
		}
		return value;
	} catch {
		return null;
	}
}

export function itemsFilter() {

	const listings = document.querySelectorAll( '.filterable-listing' );
	if ( ! listings?.length ) {
		return;
	}

	const urlParams = new URLSearchParams( window.location.search );
	const urlFilter = urlParams.get( 'filter' );
	const hasFilterQueryParam = urlParams.has( 'filter' );

	listings.forEach( ( listing ) => {
		const filters = listing.querySelector( '.filters' );
		const itemsEl = listing.querySelector( '.items' );
		if ( ! filters || ! itemsEl ) {
			return;
		}

		const runFilter = async ( link ) => {
			if ( ! link ) {
				return;
			}

			const term = link.dataset.term || 'all';
			const postType = listing.dataset.postType;
			const taxonomy = listing.dataset.taxonomy || filters.dataset.taxonomy;
			const perPage = parseInt( listing.dataset.perPage || '6', 10 );
			const emptyMessage = listing.dataset.emptyMessage || '';

			// UI state.
			filters.querySelectorAll( 'button[data-term]' ).forEach( ( button ) => button.classList.remove( 'active' ) );
			link.classList.add( 'active' );
			filters.dataset.active = term;

			listing.dataset.currentPage = '1';
			itemsEl.classList.add( 'is-loading' );

			try {
				const resText = await WPAjax( 'wr_items_filter', {
					postType,
					taxonomy,
					term,
					perPage,
					page: 1,
					emptyMessage,
				} );
				const res = JSON.parse( resText );
				if ( ! res?.success ) {
					throw new Error( res?.data?.message || 'Request failed' );
				}

				syncFilterQueryParam( term );

				const taxonomyStorageKey = filters.dataset.taxonomy;
				if ( taxonomyStorageKey ) {
					persistFilterForTaxonomy( taxonomyStorageKey, term );
				}

				itemsEl.innerHTML = res.data.html || '';

				const maxPages = String( res.data.maxPages || 1 );
				listing.dataset.maxPages = maxPages;

				const wrap = listing.querySelector( '.items-loadmore-wrap' );
				if ( wrap ) {
					if ( parseInt( maxPages, 10 ) > 1 ) {
						wrap.style.display = '';
					} else {
						wrap.style.display = 'none';
					}
				}
			} catch ( err ) {
				// eslint-disable-next-line no-console
				console.error( err );
			} finally {
				itemsEl.classList.remove( 'is-loading' );

				const filteredItems = itemsEl.querySelectorAll( '.overlay-link' );
				if ( filteredItems ) {
					filteredItems.forEach( ( overlayLink ) => {
						overlayLink.addEventListener( 'click', ( e ) => {
							e.preventDefault();
						} );
					} );
				}
			}
		};

		filters.addEventListener( 'click', async ( e ) => {
			const link = e.target?.closest?.( 'button[data-term]' );
			if ( ! link ) return;
			e.preventDefault();
			await runFilter( link );
		} );

		if ( hasFilterQueryParam ) {
			if ( urlFilter !== null && urlFilter !== '' ) {
				const match = filters.querySelector(
					`button[data-term="${ escapeAttrSelector( urlFilter ) }"]`
				);
				if ( match ) {
					void runFilter( match );
				}
			}
		} else {
			const taxonomyStorageKey = filters.dataset.taxonomy;
			const storedTerm = getStoredFilterForTaxonomy( taxonomyStorageKey );
			if ( storedTerm !== null ) {
				const match = filters.querySelector(
					`button[data-term="${ escapeAttrSelector( storedTerm ) }"]`
				);
				if ( match ) {
					void runFilter( match );
				}
			}
		}
	} );
}
